import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Users, 
  CheckCircle, 
  XCircle, 
  ToggleLeft, 
  ToggleRight,
  Phone,
  Mail,
  Building,
  Calendar,
  Activity,
  UserCheck,
  UserX,
  Search,
  Filter,
  RefreshCw,
  Shield,
  ShieldCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersManagement = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [capabilityFilter, setCapabilityFilter] = useState('all');
  const [showActivity, setShowActivity] = useState({});

  // Redirect if not admin
  useEffect(() => {
    if (!currentUser?.is_admin) {
      navigate('/dashboard');
      toast.error('Admin access required');
      return;
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, statusFilter, capabilityFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.business_name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.phone.includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => {
        if (statusFilter === 'approved') return user.is_approved;
        if (statusFilter === 'pending') return !user.is_approved;
        if (statusFilter === 'on_call') return user.is_on_call;
        if (statusFilter === 'admin') return user.is_admin;
        return true;
      });
    }

    // Capability filter
    if (capabilityFilter !== 'all') {
      filtered = filtered.filter(user => {
        if (capabilityFilter === 'can_post') return user.can_post_jobs;
        if (capabilityFilter === 'can_receive') return user.can_receive_jobs;
        if (capabilityFilter === 'both') return user.can_post_jobs && user.can_receive_jobs;
        return true;
      });
    }

    setFilteredUsers(filtered);
  };

  const updateUserApproval = async (userId, approved) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approval`, { is_approved: approved });
      toast.success(approved ? 'User approved' : 'User approval revoked');
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user approval:', error);
      toast.error('Failed to update user approval');
    }
  };

  const updateUserCapability = async (userId, field, value) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { [field]: value });
      toast.success('User capability updated');
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user capability:', error);
      toast.error('Failed to update capability');
    }
  };

  const toggleUserActivity = (userId) => {
    setShowActivity(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getStatusBadge = (user) => {
    if (user.is_admin) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 flex items-center">
        <Shield className="h-3 w-3 mr-1" />
        Admin
      </span>;
    }
    if (!user.is_approved) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 flex items-center">
        <XCircle className="h-3 w-3 mr-1" />
        Pending
      </span>;
    }
    if (user.is_on_call) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center">
        <CheckCircle className="h-3 w-3 mr-1" />
        On Call
      </span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 flex items-center">
      <UserCheck className="h-3 w-3 mr-1" />
      Approved
    </span>;
  };

  const getCapabilitiesBadge = (user) => {
    const badges = [];
    if (user.can_post_jobs) badges.push('Post');
    if (user.can_receive_jobs) badges.push('Receive');
    
    if (badges.length === 0) {
      return <span className="text-xs text-gray-400">No capabilities</span>;
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {badges.map(badge => (
          <span 
            key={badge}
            className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800"
          >
            {badge}
          </span>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <Users className="h-6 w-6 mr-2" />
                  Users Management
                </h1>
                <p className="text-sm text-gray-600">Manage user accounts and capabilities</p>
              </div>
            </div>
            
            <button
              onClick={fetchUsers}
              className="jop-button-secondary flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="jop-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Users
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="jop-input pl-10"
                  placeholder="Search by name, email, or phone"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="jop-select"
              >
                <option value="all">All Statuses</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending Approval</option>
                <option value="on_call">On Call</option>
                <option value="admin">Admins</option>
              </select>
            </div>

            {/* Capability Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Capabilities
              </label>
              <select
                value={capabilityFilter}
                onChange={(e) => setCapabilityFilter(e.target.value)}
                className="jop-select"
              >
                <option value="all">All Capabilities</option>
                <option value="can_post">Can Post Jobs</option>
                <option value="can_receive">Can Receive Jobs</option>
                <option value="both">Can Post & Receive</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                Showing {filteredUsers.length} of {users.length} users
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="jop-card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Users</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capabilities
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <Building className="h-5 w-5 text-amber-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.business_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {user.id.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center mb-1">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {user.email}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        {user.phone}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user)}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getCapabilitiesBadge(user)}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {formatDate(user.created_at)}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {/* Approval Toggle */}
                        {!user.is_admin && (
                          <button
                            onClick={() => updateUserApproval(user.id, !user.is_approved)}
                            className={`p-2 rounded-lg transition-colors ${
                              user.is_approved 
                                ? 'text-green-600 hover:bg-green-100' 
                                : 'text-red-600 hover:bg-red-100'
                            }`}
                            title={user.is_approved ? 'Revoke Approval' : 'Approve User'}
                          >
                            {user.is_approved ? <ShieldCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                          </button>
                        )}
                        
                        {/* Post Jobs Toggle */}
                        {user.is_approved && !user.is_admin && (
                          <button
                            onClick={() => updateUserCapability(user.id, 'can_post_jobs', !user.can_post_jobs)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title={user.can_post_jobs ? 'Disable Job Posting' : 'Enable Job Posting'}
                          >
                            {user.can_post_jobs ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </button>
                        )}
                        
                        {/* Receive Jobs Toggle */}
                        {user.is_approved && !user.is_admin && (
                          <button
                            onClick={() => updateUserCapability(user.id, 'can_receive_jobs', !user.can_receive_jobs)}
                            className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                            title={user.can_receive_jobs ? 'Disable Job Receiving' : 'Enable Job Receiving'}
                          >
                            {user.can_receive_jobs ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </button>
                        )}
                        
                        {/* On Call Toggle */}
                        {user.can_receive_jobs && !user.is_admin && (
                          <button
                            onClick={() => updateUserCapability(user.id, 'is_on_call', !user.is_on_call)}
                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                            title={user.is_on_call ? 'Set Off Call' : 'Set On Call'}
                          >
                            {user.is_on_call ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try adjusting your filters or search criteria.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;